/**
 * Print Check — pure resolution + aspect-ratio validation. No I/O, no DOM.
 *
 * Rules (README "Print Check thresholds", pricing v5):
 *   - Effective DPI is computed on the CROPPED area (via @rpl/pricing's
 *     effectiveDpi = min(shortPx/shortIn, longPx/longIn)).
 *   - Ratio within `ratioTolerance` (2%) of the print ratio = exact fit,
 *     otherwise a centered auto-crop is applied (amber flag). It never blocks.
 *   - Quality tiers (display only, except the floor):
 *       ≥ targetDpi (300)  → "Best quality"
 *       ≥ goodDpi   (200)  → "Good quality"
 *       ≥ family minimum   → "Prints OK at this size"
 *       below the minimum  → blocked ("Too low for this size", override allowed)
 *   - The minimum acceptable DPI is PER PRODUCT FAMILY (`minDpi`): C-Type
 *     prints small sizes fine at low res (100), fine art aims higher (180).
 *
 * Thresholds default to the seed values but callers should pass live thresholds
 * from PricingData (see thresholdsFromPricing) so an admin change takes effect.
 */

import { effectiveDpi } from '@rpl/pricing';
import type { CropRect, FitMode, PrintCheckStatus } from './types';

export { effectiveDpi };

export type Inches = readonly [number, number];

/** Product family a size belongs to (mirrors ResolvedSize.family / parseSizeId). */
export type PrintFamily = 'ctype' | 'fine_art';

export interface PrintCheckThresholds {
  /** DPI at/above which a print is "Best quality" (display only). */
  targetDpi: number;
  /** DPI at/above which a print is "Good quality" (display only). */
  goodDpi: number;
  /** Minimum acceptable DPI per product family; below = blocked (override allowed). */
  minDpi: { ctype: number; fineArt: number };
  ratioTolerance: number;
}

/** Seed thresholds; mirrors pricing.printCheck (300 / 200 / {100,180} / 0.02). */
export const DEFAULT_THRESHOLDS: PrintCheckThresholds = {
  targetDpi: 300,
  goodDpi: 200,
  minDpi: { ctype: 100, fineArt: 180 },
  ratioTolerance: 0.02,
};

/** The minimum acceptable DPI for a product family. */
export function minDpiOf(thresholds: PrintCheckThresholds, family: PrintFamily): number {
  return family === 'fine_art' ? thresholds.minDpi.fineArt : thresholds.minDpi.ctype;
}

export function thresholdsFromPricing(pc: PrintCheckThresholds): PrintCheckThresholds {
  return {
    targetDpi: pc.targetDpi,
    goodDpi: pc.goodDpi,
    minDpi: { ctype: pc.minDpi.ctype, fineArt: pc.minDpi.fineArt },
    ratioTolerance: pc.ratioTolerance,
  };
}

export type RatioFit = 'exact' | 'autocrop';

/**
 * Orientation-agnostic aspect comparison. Within tolerance → 'exact', else the
 * image would need cropping to fill the print → 'autocrop'.
 */
export function ratioFit(
  imageW: number,
  imageH: number,
  inches: Inches,
  tolerance = DEFAULT_THRESHOLDS.ratioTolerance,
): RatioFit {
  const imageRatio = Math.max(imageW, imageH) / Math.min(imageW, imageH);
  const printRatio = Math.max(inches[0], inches[1]) / Math.min(inches[0], inches[1]);
  const rel = Math.abs(imageRatio - printRatio) / printRatio;
  return rel <= tolerance ? 'exact' : 'autocrop';
}

/**
 * A print size is a PANORAMA shape when its long edge is ≥3× its short edge
 * (matches the catalog's pano:true sizes — 6×24, 8×24). Pano crops default to
 * the WIDE orientation regardless of the photo's orientation: a wide band
 * across the photo, never a tall skinny strip.
 */
export function isPanoInches(inches: Inches): boolean {
  return Math.max(inches[0], inches[1]) / Math.min(inches[0], inches[1]) >= 3;
}

/**
 * Largest centered rectangle matching the print aspect ratio, normalized to
 * [0,1]. This is the default "balanced auto-crop", trimming evenly from
 * opposite edges. Orientation follows the image (a landscape photo gets a
 * landscape crop) EXCEPT for panorama sizes (≥3:1), which always crop a wide
 * horizontal band — the user can still rotate it in the editor.
 */
export function centeredAutoCropRect(imageW: number, imageH: number, inches: Inches): CropRect {
  const printLong = Math.max(inches[0], inches[1]);
  const printShort = Math.min(inches[0], inches[1]);
  const landscape = isPanoInches(inches) ? true : imageW >= imageH;
  const targetAspect = landscape ? printLong / printShort : printShort / printLong; // w/h
  const imageAspect = imageW / imageH;

  let cropW: number;
  let cropH: number;
  if (imageAspect > targetAspect) {
    // Image is wider than the print → trim the sides.
    cropH = imageH;
    cropW = imageH * targetAspect;
  } else {
    // Image is taller than the print → trim top/bottom.
    cropW = imageW;
    cropH = imageW / targetAspect;
  }

  const w = cropW / imageW;
  const h = cropH / imageH;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

export interface PrintCheckResult {
  status: PrintCheckStatus;
  /** Effective DPI on the printed (cropped) area. */
  dpi: number;
}

/**
 * Run Print Check for one photo × size.
 *
 * @param photo      pixel dimensions (EXIF-corrected)
 * @param sizeInches print size in inches
 * @param cropRect   custom crop; null = centered auto-crop (only used in 'crop')
 * @param fitMode    'crop' fills the print (may trim); 'fit' letterboxes (no trim)
 * @param family     product family of the size — selects the minimum DPI
 *                   (C-Type 100 / fine art 180 by default)
 */
export function checkPrint(
  photo: { width: number; height: number },
  sizeInches: Inches,
  cropRect: CropRect | null,
  fitMode: FitMode,
  family: PrintFamily,
  thresholds: PrintCheckThresholds = DEFAULT_THRESHOLDS,
): PrintCheckResult {
  const { width, height } = photo;
  const ratio = ratioFit(width, height, sizeInches, thresholds.ratioTolerance);

  let cropWpx: number;
  let cropHpx: number;
  if (fitMode === 'fit') {
    // Whole image is printed (with white borders) → full resolution applies.
    cropWpx = width;
    cropHpx = height;
  } else if (cropRect) {
    cropWpx = Math.round(width * cropRect.w);
    cropHpx = Math.round(height * cropRect.h);
  } else {
    const r = centeredAutoCropRect(width, height, sizeInches);
    cropWpx = Math.round(width * r.w);
    cropHpx = Math.round(height * r.h);
  }

  const dpi = effectiveDpi(cropWpx, cropHpx, [sizeInches[0], sizeInches[1]]);

  let status: PrintCheckStatus;
  if (dpi < minDpiOf(thresholds, family)) {
    status = 'lowres';
  } else if (fitMode !== 'fit' && ratio === 'autocrop') {
    status = 'autocrop';
  } else {
    status = 'ok';
  }

  return { status, dpi };
}
